import { useMemo } from 'react';
import {
  DEVICE_TYPE_LABELS_UK,
  type DiagramNode,
  type MonitorMode,
} from '@ukrop-nm/shared';
import { DeviceIcon } from '../components/DeviceIcon';
import { useDiagram } from '../hooks/useDiagram';
import { useIconPack } from '../icons/IconPackContext';
import { UkropTip } from '../tooltips/DiagramTooltipContext';

const MODE_UK: Record<MonitorMode, string> = {
  passive: 'пасивний',
  icmp: 'ICMP',
  tcp: 'TCP',
  http: 'HTTP',
  dhcp_presence: 'DHCP',
  arp_presence: 'ARP',
  wifi_registration: 'Wi‑Fi',
  router_interface: 'інтерфейс',
  neighbor: 'сусід',
  derived: 'похідний',
  manual: 'вручну',
};

const UNBOUND = [{ monitorKey: 'OBS-PRINTER-01', note: 'Побачено в симуляторі, не на схемі' }];

function zoneTitle(nodes: DiagramNode[], zoneId: string | null | undefined): string {
  if (!zoneId) return 'Без зони';
  return nodes.find((n) => n.id === zoneId)?.label ?? 'Без зони';
}

export function DevicesPage() {
  const diagram = useDiagram();
  const { pack } = useIconPack();

  const groups = useMemo(() => {
    const devices = diagram.nodes.filter((n) => n.type !== 'zone');
    const order: string[] = [];
    const map = new Map<string, DiagramNode[]>();
    for (const n of devices) {
      const key = n.zoneId || '_none';
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(n);
    }
    return order.map((key) => ({
      id: key,
      title: key === '_none' ? 'Без зони' : zoneTitle(diagram.nodes, key),
      nodes: map.get(key) ?? [],
    }));
  }, [diagram.nodes]);

  return (
    <div className="p-4 md:p-6">
      <h2 className="page-title">Обладнання / інвентаризація</h2>
      <p className="notice mt-4">
        Знайдені пристрої не додаються на схему автоматично. Підтвердіть привʼязку вручну.
      </p>

      <div className="mt-4 grid items-start gap-4 xl:grid-cols-2">
        <section className="card min-w-0">
          <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">На схемі</h3>
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.id}>
                <h4 className="mb-1 text-theme-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                  {g.title}
                  <span className="ml-2 font-normal normal-case">({g.nodes.length})</span>
                </h4>
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {g.nodes.map((n) => (
                    <li key={n.id} className="flex items-start gap-3 py-2.5">
                      <span className="mt-0.5 shrink-0 text-brand-500 dark:text-brand-400">
                        <DeviceIcon type={n.type} pack={pack} size={28} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium break-words text-gray-900 dark:text-gray-50">
                          {n.label}
                        </div>
                        <div className="mt-0.5 text-theme-xs text-gray-500 dark:text-gray-400">
                          {DEVICE_TYPE_LABELS_UK[n.type]}
                          {n.inventory?.model ? ` · ${n.inventory.model}` : ''}
                          {n.inventory?.ipLocal ? ` · ${n.inventory.ipLocal}` : ''}
                        </div>
                      </div>
                      <div className="max-w-[42%] shrink-0 text-right">
                        {n.monitorKey ? (
                          <code className="inline-block max-w-full truncate rounded-md bg-gray-100 px-1.5 py-0.5 text-theme-xs text-gray-800 dark:bg-gray-800 dark:text-gray-100">
                            {n.monitorKey}
                          </code>
                        ) : null}
                        <div className="mt-1 text-theme-xs text-gray-500 dark:text-gray-400">
                          {MODE_UK[n.monitorMode] ?? n.monitorMode}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="card min-w-0">
          <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">
            Спостережені, не привʼязані
          </h3>
          <ul className="space-y-3">
            {UNBOUND.map((u) => (
              <li
                key={u.monitorKey}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm dark:border-gray-700 dark:bg-gray-950"
              >
                <span className="shrink-0 text-brand-500 dark:text-brand-400">
                  <DeviceIcon type="printer" pack={pack} size={28} />
                </span>
                <div className="min-w-0 flex-1">
                  <code className="font-medium text-gray-900 dark:text-gray-50">{u.monitorKey}</code>
                  <div className="text-theme-xs text-gray-500 dark:text-gray-400">{u.note}</div>
                </div>
                <UkropTip text="Фаза 3+">
                  <button type="button" className="btn-sm" disabled>
                    Привʼязати…
                  </button>
                </UkropTip>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
